import { useState } from 'react';
import { TextInput, Textarea } from '@mantine/core';
import { IconTrash, IconPencil, IconCheck, IconX } from '@tabler/icons-react';
import type { SupplementLocal } from '../../types/medications';

interface SupplementFormProps {
  supplement: SupplementLocal;
  onChange: (updated: SupplementLocal) => void;
  onRemove: () => void;
  onSubmit: () => void;
  disabled?: boolean;
  isNew?: boolean;
}

export function SupplementForm({
  supplement,
  onChange,
  onRemove,
  onSubmit,
  disabled = false,
  isNew = false,
}: SupplementFormProps) {
  const [isEditing, setIsEditing] = useState(isNew);

  const handleChange = (
    field: keyof SupplementLocal,
    value: string | undefined
  ) => {
    onChange({ ...supplement, [field]: value });
  };

  const handleSave = () => {
    if (supplement.supplementName.trim()) {
      onSubmit();
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    if (isNew) {
      onRemove();
    } else {
      setIsEditing(false);
    }
  };

  // View mode - display as card
  if (!isEditing) {
    return (
      <div className="card p-4 group">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="font-medium text-gray-200">{supplement.supplementName}</h4>
            <div className="mt-1 text-sm text-gray-400 space-y-0.5">
              {(supplement.dosage || supplement.frequency) && (
                <p>
                  {[supplement.dosage, supplement.frequency].filter(Boolean).join(' — ')}
                </p>
              )}
              {supplement.comments && (
                <p className="text-gray-500 italic">{supplement.comments}</p>
              )}
            </div>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              disabled={disabled}
              className="p-1.5 rounded-lg text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors disabled:opacity-50"
              title="Edit"
            >
              <IconPencil size={16} />
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
              title="Delete"
            >
              <IconTrash size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Edit mode - show form
  return (
    <div className="card p-4 border-cyan-500/30">
      <div className="space-y-4">
        {/* Supplement Name */}
        <TextInput
          label="Supplement Name"
          placeholder="e.g., Vitamin D, Fish Oil, Magnesium"
          value={supplement.supplementName}
          onChange={(e) => handleChange('supplementName', e.target.value)}
          disabled={disabled}
          required
          classNames={{
            input: 'input',
            label: 'text-sm font-medium text-gray-300 mb-1',
          }}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Dosage */}
          <TextInput
            label="Dosage"
            placeholder="e.g., 1000 IU, 500mg, 2 capsules"
            value={supplement.dosage || ''}
            onChange={(e) => handleChange('dosage', e.target.value || undefined)}
            disabled={disabled}
            classNames={{
              input: 'input',
              label: 'text-sm font-medium text-gray-300 mb-1',
            }}
          />

          {/* Frequency */}
          <TextInput
            label="Frequency"
            placeholder="e.g., Once daily, With breakfast"
            value={supplement.frequency || ''}
            onChange={(e) => handleChange('frequency', e.target.value || undefined)}
            disabled={disabled}
            classNames={{
              input: 'input',
              label: 'text-sm font-medium text-gray-300 mb-1',
            }}
          />
        </div>

        {/* Comments */}
        <Textarea
          label="Comments"
          placeholder="Share anything helpful — e.g., why you take it, any noticeable benefits"
          value={supplement.comments || ''}
          onChange={(e) => handleChange('comments', e.target.value || undefined)}
          disabled={disabled}
          minRows={2}
          classNames={{
            input: 'input',
            label: 'text-sm font-medium text-gray-300 mb-1',
          }}
        />

        {/* Action buttons */}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={disabled}
            className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1"
          >
            <IconX size={14} />
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={disabled || !supplement.supplementName.trim()}
            className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <IconCheck size={14} />
            {isNew ? 'Add Supplement' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
